import * as React from 'react';
import styled, { CSSObject } from 'styled-components';
import { Lazy } from 'fp-ts/lib/function';
import { useIORef } from '../../core/hooks';
import { safeFocus } from '../../core/Env';
import { splitClasses } from '../utils';
import { useSetter, useGetter, useEffect } from './slots';
import { ctxSelectClass } from './shared';
import { Value } from './types';

import { pipe } from 'fp-ts/lib/function';
import * as A from 'fp-ts/lib/Array';
import * as O from 'fp-ts/lib/Option';

import { 
    makeLayout, makeLayoutSlot, Effects,
    makeEntry,
    IControllerContext, IPluginContext, ISlotContext, 
} from '../../core/Slot';

export const URI = 'Select_list';
export type URI = typeof URI;
declare module '../../core/Slot' {
    interface URItoKind {
        [URI]: {
            slots: {
                item: {
                    outerProps: {
                        children: (x: Value) => React.ReactNode,
                        className?: string,
                    },
                    innerProps: {
                        key: number,
                        index: number,
                        value: Value,
                        onClick: (x: Value, name: string) => Lazy<void>,
                    },
                },
                error: {
                    outerProps: {},
                    innerProps: {},
                },
                acc: {
                    outerProps: { className?: string, children?: React.ReactNode, },
                    innerProps: {},
                },
            },
            controllers: {},
            effects: [],
            plugins: {},
        },
    }
}

const SlotContext = React.createContext({} as ISlotContext<URI>);
const ControllerContext = React.createContext({} as IControllerContext<URI>);
const EffectContext = React.createContext({} as Effects<URI>);
const PluginContext = React.createContext({} as IPluginContext<URI>);

const Layout = makeLayout(SlotContext);
const listSlot = makeLayoutSlot(SlotContext);
const Entry = makeEntry({
    SlotContext,
    ControllerContext,
    EffectContext,
    PluginContext,
    initSlots: {
        item: {
            props: {
                key: () => 0,
                index: () => 0,
                value: () => ({ id: 1, text: 'Errored item!' }),
                children: () => () => null,
                onClick: () => () => () => {},
            },
            component: (x) => <div>{x}</div>
        },
        error: {
            props: {},
            component: () => <span>Данных нет!</span>
        },
        acc: {
            props: { children: () => <Typography>Выбранные значения:</Typography> },
            component: () => null,
        },
    },
    initControllers: {},
    initEffect: [],
});



const active = ({ isActive }: { isActive?: boolean }): CSSObject => ({
    backgroundColor: isActive ? '#f5f5f5' : undefined,
});

const ItemS = styled.div<{ isActive?: boolean }>`
    padding: 3px 10px;
    margin: 3px 0;
    line-height: 1.4;
    cursor: pointer;
    position: relative;
    ${active};

    &:hover,
    &:focus {
        background-color: #f5f5f5;
    }
`;

const useControlItem1 = (itemRef: React.RefObject<HTMLDivElement>, index: number) => {
    const select =              useGetter('select');
    const cursorController =    useGetter('cursor');
    const [cursorMove] =        useEffect('cursorMove');

    React.useEffect(() => {

        const cursorPosition = cursorController.get();

        if(index === cursorPosition && select.isActive()){
            setTimeout(safeFocus(itemRef), 1);
        }

    }, [cursorMove, index]);
}

const useControlItem2 = (itemRef: React.RefObject<HTMLDivElement>, index: number) => {
    const select =              useGetter('select');
    const cursorController =    useGetter('cursor');
    const [cursorMove] =        useEffect('cursorMove');
    const target =              useGetter('target');

    React.useEffect(() => {

        const cursorPosition = cursorController.get();

        if(index === cursorPosition && select.isActive()){
            safeFocus(itemRef);
            target.active();
        }

    }, [cursorMove, index]);
}

const useIsActive = (index: number) => {
    const cursorController = useGetter('cursor');
    const [cursorMove] = useEffect('cursorMove');

    return React.useMemo(() => {
        const cursorPosition = cursorController.get();

        return index === cursorPosition;
    }, [cursorMove, index]);
}

const useHandleClick = (handler: (x: Value, name: string) => Lazy<void>, value: Value, index: number) => {
    const service =             useGetter('service');
    const cursorController =    useGetter('cursor');

    const clickable = handler(value, service.name);

    return () => {
        cursorController.set(index);
        clickable();
    }
}


export const Item = listSlot('item')(({ children, index, value, onClick, className }) => {
    const itemRef = React.useRef<HTMLDivElement>(null);

    useControlItem2(itemRef, index);
    const isActive = useIsActive(index);
    const clickHandler = useHandleClick(onClick, value, index);

    return (
        <ItemS
            ref={itemRef}
            key={index}
            tabIndex={0}
            onMouseDown={clickHandler}
            isActive={isActive}
            className={splitClasses(ctxSelectClass('item'), className, isActive ? 'isActive' : undefined)}>
            {children(value)}
        </ItemS>
    );
});

export const Item2 = listSlot('item')(({ children, index, value, onClick, className }) => {
    const itemRef = React.useRef<HTMLDivElement>(null);

    useControlItem1(itemRef, index);
    const clickHandler = useHandleClick(onClick, value, index);

    return (
        <ItemS
            ref={itemRef}
            key={index}
            tabIndex={0}
            onMouseDown={clickHandler}
            isActive={false}
            className={splitClasses(ctxSelectClass('item'), className)}>
            {children(value)}
        </ItemS>
    );
});

const ErrorS = styled.div`
    padding: 10px 20px;
`;

export const Error = listSlot('error')(({ children }) => {
    return (
        <React.Fragment>
            <ErrorS className={ctxSelectClass('error')}>
                {children}
            </ErrorS>
        </React.Fragment>
    );
})

type ListProps = {
    children: React.ReactNode,
    data: Record<string, any>[],
}

export const List = ({ children, data }: ListProps) => {
    

    const derivedData = React.useMemo(() => data || [], [data]);

    const dropdownController =  useGetter('dropdown');
    const select =              useGetter('select');
    const service =             useGetter('service');
    const target =              useGetter('target');

    const {
        read: getMutableData,
        write: changeMutableData,
    } = useIORef(derivedData);

    const [s, runChangeData] = useEffect('changeData');

    const changeData = runChangeData((data: Record<string, any>[]) => {
        changeMutableData(data)();
    })

    React.useEffect(() => {
        changeData(derivedData);
    }, [derivedData]);

    


    const handleClick = (value: Record<string, any>, name: string) => () => {
        service.onSelect(value, name);
        target.change(value);
        dropdownController.close();
        setTimeout(() => {
            select.off();
        }, 0);
    }


    useSetter('dropdownTarget', {
        getData: () => derivedData,
        getMutableData,
        changeData,
    });

    const list = React.useMemo(() => {
        const data = getMutableData();

        return data.length === 0 
            ? <Layout name="error" /> 
            : data.map((d, i) => (
                <Layout 
                    name="item"
                    key={i} 
                    index={i}
                    value={d}
                    onClick={handleClick}
                />
            ));
    }, [s]);

    return (
        <Entry connectLayout={children}>
            {list}
        </Entry>
    );
};

export const Acc = listSlot('acc')(({ className, children }) => {

    const multi = useGetter('multi');
    const dropdownTarget = useGetter('dropdownTarget');
    const service = useGetter('service');

    const remove = (x: Record<string, any>) => () => {
        multi.removeAt(x.id);
        dropdownTarget.modifyData(xs => [x, ...xs]);
        service.onSelect(multi.get(), service.name);
    }

    const x =  multi.get();

    if(x.length === 0)
        return null;

    return (
        <AccS className={className}>
            {children}
            {x.map((item, i) => {
                return (
                    <Layout
                        name="item"
                        key={i} 
                        index={i}
                        value={item}
                        onClick={remove}
                    />
                );
            })}
        </AccS>
    );
});

const Typography = styled.p`
    padding: 8px 10px 3px;
    font-weight: 600;
    font-size: 10px;
`;

const AccS = styled.div`

`;


export const MultiList = ({ children, data }: ListProps) => {
    
    const [multiChange] = useEffect('multiChange');
    const multi = useGetter('multi');

    const derivedData = React.useMemo(() => {
        const values = multi.get();

        if(!data){
            return [];
        }

        const result = data.reduce((acc, curr) => {
            // @ts-ignore
            const r = values.find(v => v.id === curr.id) === undefined ? [curr] : [];
            return acc.concat(r)
        }, [] as typeof data);

        return result as Record<string, any>[];
    }, [data]);

    const {
        read: getMutableData,
        write: changeMutableData,
        modify: modifyMutableData,
    } = useIORef(derivedData);

    const [s, runChangeData] = useEffect('changeData');
    const service = useGetter('service');

    const changeData = runChangeData((data: Record<string, any>[]) => {
        changeMutableData(data as any)();
    });

    type D = Record<string, any>;
    type F = (data: D[]) => D[];

    const modifyData = runChangeData((f: F) => {
        modifyMutableData(f)();
    });

    
    const handleClick = (value: Record<string, any>, name: string) => () => {
        multi.append(value);
        modifyData((data: any) => pipe(
            data as D[],
            A.findIndex(el => el.id === value.id),
            O.chain((index: number) => A.deleteAt(index)(data)),
            O.fold(
                () => data,
                (r: any) => r,
            )
        ));
        service.onSelect(multi.get(), name);
    }

    React.useEffect(() => {
        changeData(derivedData);
    }, [derivedData]);


    useSetter('dropdownTarget', {
        getData: () => derivedData,
        getMutableData,
        changeData,
        modifyData,
    });

    const checked = React.useMemo(() => {
        return <Layout name="acc" />;
    }, [multiChange]);

    const list = React.useMemo(() => {
        const data = getMutableData();

        return data.length === 0 
            ? <Layout name="error" /> 
            : data.map((d, i) => (
                <Layout 
                    name="item"
                    key={i} 
                    index={i}
                    value={d}
                    onClick={handleClick}
                />
            ));
    }, [s]);

    return (
        <Entry connectLayout={children}>
            {checked}
            {list}
        </Entry>
    );
};


export const SingleList = ({ children, data }: ListProps) => {
    
    const [multiChange] = useEffect('multiChange');
    const multi = useGetter('multi');

    const derivedData = React.useMemo(() => {
        if(!data){
            return [];
        }
        return data;
    }, [data]);

    const {
        read: getMutableData,
        write: changeMutableData,
        modify: modifyMutableData,
    } = useIORef(derivedData);

    const [s, runChangeData] = useEffect('changeData');
    const service = useGetter('service');

    const changeData = runChangeData((data: Record<string, any>[]) => {
        changeMutableData(data as any)();
    });

    type D = Record<string, any>;
    type F = (data: D[]) => D[];

    const modifyData = runChangeData((f: F) => {
        modifyMutableData(f)();
    });

    
    const handleClick = (value: Record<string, any>, name: string) => () => {
        multi.clear();
        multi.append(value);
        service.onSelect(multi.get(), name);
    }

    React.useEffect(() => {
        changeData(derivedData);
    }, [derivedData]);


    useSetter('dropdownTarget', {
        getData: () => derivedData,
        getMutableData,
        changeData,
        modifyData,
    });

    const checked = React.useMemo(() => {
        return <Layout name="acc" />;
    }, [multiChange]);

    const list = React.useMemo(() => {
        const data = getMutableData();

        return data.length === 0 
            ? <Layout name="error" /> 
            : data.map((d, i) => (
                <Layout 
                    name="item"
                    key={i} 
                    index={i}
                    value={d}
                    onClick={handleClick}
                />
            ));
    }, [s]);

    return (
        <Entry connectLayout={children}>
            {checked}
            {list}
        </Entry>
    );
};
